// CompaniesPanel.js
import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Search } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useApi2Resource from '../../hooks/useApi2Resource';
import usePagedList from '../../hooks/usePagedList';
import { Bars, FeedCard, FeedSection, FeedSegments, LoadMore, PanelState } from '../feed/FeedCard';
import {
  companyPath,
  fetchInfluenceOverview,
  formatCount,
  formatDollars,
  formatPercent,
  formatScore,
  COMPANY_SEARCH_PAGE_SIZE,
  searchCompanies,
  titleCaseName,
} from '../../utils/spending';

/**
 * CompaniesPanel
 *
 * The companies behind the money, from the weekly companies dataset: every
 * leaderboard /influence/overview returns, and a search box. Every name opens
 * the company page.
 *
 * ─── Leaderboards (guide, section 4) ──────────────────────────────────────────
 *
 * `leaderboards` is an object of ranked lists, each { title, field, results },
 * keyed by board. It is read as an object, and as an array if the API ever
 * returns one. `field` is the key read from every row; the other fields on a
 * row give it its one-line context.
 *
 * The boards are grouped by direction, one color each, and none red or green,
 * because none of the measures is a verdict:
 *
 *   gov   teal     government to company: contracts, contracts not competed,
 *                  largest agency share, government reliance score
 *   dep   amber    company to government: dependence on federal contracts
 *   inf   indigo   company to policy: lobbying, lobbyists, PAC giving,
 *                  influence activity score
 *
 * A board this file does not know yet still shows, under More, with the
 * API's own title and a format read from its field name. So a new board in
 * the response appears without a code change, rather than being dropped.
 *
 * Money bars scale to the largest row, scores to 100 and the agency share to
 * 100%, so a score of 50 fills half the track wherever it appears. Dependence
 * scales to its largest row, since it can run past 100%.
 *
 * The boards that need a sentence get it next to the number, not in a
 * footnote: dependence can exceed 100%; the largest agency share is not the
 * agency paying most; the influence activity score ranks disclosed activity,
 * not results; a PAC is not its company.
 *
 * Only companies are ranked. Universities and laboratory operators count in
 * the totals but not on the boards, and the card says so.
 */

const BOARDS = [
  { key: 'contracts', group: 'gov', tone: 'gov', kind: 'money' },
  { key: 'contracts_noncompeted', group: 'gov', tone: 'gov', kind: 'money' },
  { key: 'top_agency_share', group: 'gov', tone: 'gov', kind: 'percent', max: 1 },
  { key: 'reliance_score', group: 'gov', tone: 'gov', kind: 'score', max: 100 },
  { key: 'dependence_ratio', group: 'dep', tone: 'dep', kind: 'percent' },
  { key: 'lobbying', group: 'inf', tone: 'inf', kind: 'money' },
  { key: 'lobbyists', group: 'inf', tone: 'inf', kind: 'count' },
  { key: 'pac_total', group: 'inf', tone: 'inf', kind: 'money' },
  { key: 'influence_score', group: 'inf', tone: 'inf', kind: 'score', max: 100 },
];

const GROUPS = ['gov', 'dep', 'inf', 'other'];

const SEARCH_DEBOUNCE_MS = 300;

// For a board this file has no entry for: the format is read from the field
// name, the way the known boards' fields are named.
const inferConfig = (key, field) => {
  const name = field || key;
  if (/score$/.test(name)) return { key, group: 'other', tone: 'kind', kind: 'score', max: 100 };
  if (/(share|ratio)$/.test(name)) return { key, group: 'other', tone: 'kind', kind: 'percent' };
  if (/(lobbyists|officials|count|comments|trades)$/.test(name)) {
    return { key, group: 'other', tone: 'kind', kind: 'count' };
  }
  return { key, group: 'other', tone: 'kind', kind: 'money' };
};

// Every board in the response, known ones in BOARDS order, unknown ones after.
const normalizeBoards = (raw) => {
  let entries = [];
  if (Array.isArray(raw)) {
    entries = raw.map((board, index) => [board?.key || board?.field || String(index), board]);
  } else if (raw && typeof raw === 'object') {
    entries = Object.entries(raw);
  }

  const order = (config) => {
    const index = BOARDS.findIndex((known) => known.key === config.key);
    return index === -1 ? BOARDS.length : index;
  };

  return entries
    .filter(([, board]) => board && Array.isArray(board.results))
    .map(([key, board]) => {
      const field = board.field || key;
      const config =
        BOARDS.find((known) => known.key === key) ||
        BOARDS.find((known) => known.key === field) ||
        inferConfig(key, field);
      return { key, field, title: board.title || null, rows: board.results, config };
    })
    .sort((a, b) => order(a.config) - order(b.config));
};

const FORMATTERS = {
  money: (value) => formatDollars(value),
  percent: (value) => formatPercent(value),
  score: (value) => formatScore(value),
  count: (value) => formatCount(value),
};

function CompaniesPanel() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [group, setGroup] = useState('gov');
  const [boardKey, setBoardKey] = useState(null);
  const [query, setQuery] = useState('');
  // The query the list is for: the box's text once typing pauses, so "lock"
  // fires one request rather than four.
  const [needle, setNeedle] = useState('');

  const overview = useApi2Resource(() => fetchInfluenceOverview(), []);
  const boards = normalizeBoards(overview.data?.leaderboards);

  const groups = GROUPS.filter((id) => boards.some((board) => board.config.group === id));
  const activeGroup = groups.includes(group) ? group : groups[0] || null;
  const groupBoards = boards.filter((board) => board.config.group === activeGroup);
  const active = groupBoards.find((board) => board.key === boardKey) || groupBoards[0] || null;
  const rows = active ? active.rows : [];

  // A new direction opens at its first board rather than keeping a key that
  // belongs to another group.
  const selectGroup = (id) => {
    setGroup(id);
    setBoardKey(null);
  };

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setNeedle('');
      return undefined;
    }
    const timer = setTimeout(() => setNeedle(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Pages by offset against the response's total. A new needle starts over,
  // and a slow answer for an older one is dropped when it lands.
  const results = usePagedList(
    needle
      ? (pageIndex) =>
          searchCompanies(needle, COMPANY_SEARCH_PAGE_SIZE, pageIndex * COMPANY_SEARCH_PAGE_SIZE).then(
            (body) => {
              const pageRows = Array.isArray(body?.results) ? body.results : [];
              const total = typeof body?.total === 'number' ? body.total : null;
              return {
                rows: pageRows,
                hasMore:
                  total !== null
                    ? (pageIndex + 1) * COMPANY_SEARCH_PAGE_SIZE < total
                    : pageRows.length === COMPANY_SEARCH_PAGE_SIZE,
                meta: body,
              };
            }
          )
      : null,
    [needle],
    { keyOf: (row) => row.org_id }
  );
  // Waiting on the pause counts as loading, so the spinner shows from the
  // first keystroke rather than after it.
  const searchLoading = results.loading || (query.trim().length >= 2 && query.trim() !== needle);

  const groupLabel = (id) =>
    ({
      gov: t('common:boardGroupGov', 'Contracts'),
      dep: t('common:boardGroupDep', 'Dependence'),
      inf: t('common:boardGroupInf', 'Lobbying and PACs'),
      other: t('common:boardGroupOther', 'More'),
    })[id] || id;

  // Short labels for the tabs. The board's own title from the API heads the
  // list itself.
  const boardLabel = (board) =>
    ({
      contracts: t('common:boardContracts', 'Largest contracts'),
      contracts_noncompeted: t('common:boardNoncompeted', 'Not competed'),
      top_agency_share: t('common:boardAgencyShare', 'Agency share'),
      reliance_score: t('common:boardReliance', 'Relied on most'),
      dependence_ratio: t('common:boardDependence', 'Most dependent'),
      lobbying: t('common:boardLobbying', 'Most lobbying'),
      lobbyists: t('common:boardLobbyists', 'Most lobbyists'),
      pac_total: t('common:boardPac', 'Largest PAC giving'),
      influence_score: t('common:boardInfluence', 'Influence activity'),
    })[board.config.key] ||
    board.title ||
    board.key;

  const boardNote = (board) =>
    ({
      contracts: t(
        'common:boardContractsNote',
        'Federal contract obligations, and each company\u2019s share of all of them.'
      ),
      contracts_noncompeted: t(
        'common:boardNoncompetedNote',
        'Contract obligations awarded without competition, and the share of each company\u2019s contracts that were not competed.'
      ),
      top_agency_share: t(
        'common:boardAgencyShareNote',
        'The agency where the company holds its largest share of contracts. That is not always the agency that pays it most.'
      ),
      reliance_score: t(
        'common:boardRelianceNote',
        'How much the government relies on the company as a supplier, out of 100.'
      ),
      dependence_ratio: t(
        'common:boardDependenceNote2',
        'Federal contracts as a share of the company\u2019s revenue. It can exceed 100%, because obligations are paid over years. Ratios above 200% are left out, because they usually mean a unit is filed under the wrong parent.'
      ),
      lobbying: t(
        'common:boardLobbyingNote2',
        'Lobbying spending the company reported: its own in-house report where it files one, otherwise the fees its outside firms reported.'
      ),
      lobbyists: t(
        'common:boardLobbyistsNote',
        'Lobbyists working on the company\u2019s behalf, and how many disclosed a prior government job.'
      ),
      pac_total: t(
        'common:boardPacNote',
        'What the company\u2019s PAC gave. A PAC is not its company; this is not the company\u2019s own money.'
      ),
      influence_score: t(
        'common:boardInfluenceNote',
        'Ranks disclosed lobbying, lobbyists and PAC giving, out of 100. It describes activity, not whether any of it worked.'
      ),
    })[board.config.key] || null;

  // Each board's one-line context, from the other fields every row carries.
  const subFor = (board) => (row) => {
    switch (board.config.key) {
      case 'contracts':
        return typeof row.share_of_federal === 'number'
          ? t('common:shareOfFederal', '{{share}} of all federal contract obligations', {
              share: formatPercent(row.share_of_federal),
            })
          : null;
      case 'contracts_noncompeted':
        return typeof row.noncompeted_share === 'number'
          ? t('common:noncompetedShare', '{{share}} of its contracts not competed', {
              share: formatPercent(row.noncompeted_share),
            })
          : null;
      case 'top_agency_share':
        return row.top_agency
          ? t('common:ofAgencyContracts', 'Share of contracts at {{agency}}', { agency: row.top_agency })
          : null;
      case 'dependence_ratio':
        return row.contracts || row.revenue
          ? t('common:contractsAndRevenue', '{{contracts}} contracts, {{revenue}} revenue', {
              contracts: formatDollars(row.contracts),
              revenue: formatDollars(row.revenue),
            })
          : null;
      case 'lobbying':
        if (row.lobbying_basis === 'in_house') {
          return t('common:lobbyingBasisInHouse', 'Its own in-house report');
        }
        if (row.lobbying_basis) {
          return t('common:lobbyingBasisOutside', 'Fees its outside firms reported');
        }
        return null;
      case 'lobbyists':
        return typeof row.former_officials === 'number'
          ? t('common:formerOfficials', '{{count}} disclosed a prior government job', {
              count: formatCount(row.former_officials),
            })
          : null;
      case 'pac_total':
        return typeof row.pac_to_candidates === 'number'
          ? t('common:pacToCandidates', '{{amount}} to candidates', {
              amount: formatDollars(row.pac_to_candidates),
            })
          : null;
      default:
        return null;
    }
  };

  const searching = query.trim().length >= 2;

  return (
    <FeedSection
      id="companies"
      title={t('common:companiesTitle', 'The companies behind the money')}
      subtitle={t(
        'common:companiesSubtitle',
        'Contracts, revenue, lobbying and PAC giving, joined weekly from USAspending, the SEC, Senate lobbying filings and the FEC. A dash means none recorded.'
      )}
    >
      <FeedCard>
        <label className="company-search">
          <Search size={14} aria-hidden />
          <input
            type="search"
            value={query}
            placeholder={t('common:searchCompanies', 'Find a company or ticker')}
            onChange={(event) => setQuery(event.target.value)}
            aria-label={t('common:searchCompanies', 'Find a company or ticker')}
          />
          {searchLoading && <Spinner animation="border" size="sm" />}
        </label>

        {searching ? (
          <>
            <ul className="company-results">
              {results.rows.map((row) => (
                <li key={row.org_id}>
                  <button
                    type="button"
                    className="company-result"
                    onClick={() => navigate(companyPath(row.org_id))}
                  >
                    <span className="company-result-name">
                      {titleCaseName(row.name)}
                      {Array.isArray(row.tickers) && row.tickers.length > 0 && (
                        <span className="company-result-ticker">{row.tickers.join(', ')}</span>
                      )}
                    </span>
                    <span className="company-result-value">
                      {typeof row.contracts === 'number'
                        ? t('common:contractsAmount', '{{amount}} in contracts', {
                            amount: formatDollars(row.contracts),
                          })
                        : '\u2014'}
                    </span>
                  </button>
                </li>
              ))}
              {!searchLoading && needle && !results.error && !results.rows.length && (
                <li className="spending-hint">
                  {t('common:noCompanyFound', 'No company in the dataset matches that.')}
                </li>
              )}
              {!searchLoading && results.error && (
                <li className="spending-hint">
                  {t('common:panelLoadError', 'This could not be loaded right now.')}
                </li>
              )}
            </ul>
            {!searchLoading && (
              <LoadMore
                hasMore={results.hasMore}
                loading={results.loadingMore}
                error={results.moreError}
                onClick={results.loadMore}
                label={t('common:loadMoreCompanies', 'More companies')}
              />
            )}
          </>
        ) : (
          <>
            {groups.length > 0 && (
              <div className="spending-feed-controls">
                <FeedSegments
                  label={t('common:boardDirection', 'Direction')}
                  value={activeGroup}
                  onChange={selectGroup}
                  options={groups.map((id) => ({ id, label: groupLabel(id) }))}
                />
                {/* One board in a direction needs no second row. */}
                {groupBoards.length > 1 && (
                  <FeedSegments
                    label={t('common:companyBoards', 'Rank companies by')}
                    value={active?.key}
                    onChange={setBoardKey}
                    options={groupBoards.map((board) => ({ id: board.key, label: boardLabel(board) }))}
                  />
                )}
              </div>
            )}

            <PanelState
              loading={overview.loading}
              error={overview.error}
              empty={!overview.loading && !overview.error && !rows.length}
            />

            {active && rows.length > 0 && (
              <div className="spending-list">
                {active.title && <h3 className="spending-list-title">{active.title}</h3>}
                {boardNote(active) && <p className="spending-hint">{boardNote(active)}</p>}
                <Bars
                  rows={rows}
                  tone={active.config.tone}
                  max={active.config.max}
                  share={() => null}
                  value={(row) => row[active.field]}
                  name={(row) => titleCaseName(row.name)}
                  format={FORMATTERS[active.config.kind] || FORMATTERS.money}
                  sub={subFor(active)}
                  onSelect={(row) => row.org_id && navigate(companyPath(row.org_id))}
                />
                <p className="spending-hint" style={{ marginTop: '0.9rem', marginBottom: 0 }}>
                  {t(
                    'common:boardsCompaniesOnly',
                    'Only companies are ranked. Universities and laboratory operators count toward the totals but are not ranked.'
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </FeedCard>
    </FeedSection>
  );
}

export default CompaniesPanel;